import ReadMoreBtn from '@Blocks/_components/ReadMore';
import { getArchiveBtnSettingsToCSS, getReadMoreBtnSettingsToCSS } from '@Blocks/_lib/btns';

import { type CardEventsType, type EventsTypeFront } from '@/src/Types/Blocks/Events';
import { type HeadingType } from '@/src/Types/Shared/Heading';
import { type QueryPost } from '@/src/Types/Shared/Query';
import { Button } from '@/src/Views/Shared/Button/Button';
import Heading from '@/src/Views/Shared/Heading/Heading';
import {
	DateIcon,
	DateIconFilled,
	LocalizationIcon,
	LocalizationIconFilled,
	PersonIcon,
	PersonIconFilled,
	PriceIcon,
	PriceIconFilled,
	TagIcon,
	TagIconFilled,
} from '@/src/Views/Shared/Icons';
import { Link } from '@inertiajs/react';

export default function EventsTitleImg(props: EventsTypeFront) {
	const {
		id,
		title,
		titleFormat,
		description,
		posts,
		textLabel = 'Read More',
		containerType,
		cardType,
		headingAlign,
		archiveLink,
		linkIndex,
		readMoreStyle,
		headingFontClassName,
		contentFontClass,
		context,
		archiveLinkStyles,
		iconType,
		displayExcerpt,
		displayDate,
		displayPlace,
		displayOrganizer,
		displayTag,
		displayPrice,
		sectionDecoration,
	} = props;

	const headingProps: HeadingType = {
		title: title,
		description: description,
		titleFormat: titleFormat,
		headingAlign: headingAlign,
		headingFontClassName: headingFontClassName,
		context: context,
	};

	let blockStyle = '';
	if (containerType === 'block') {
		blockStyle =
			'desktop:p-12 tablet:p-5 p-4 bg-minos-events-bg-container-color border-minos-events-container-border-width border-minos-events-container-border-color rounded-minos-events-container';
	}

	let padding = '';
	if (sectionDecoration?.isSectionDecorated && sectionDecoration.type === 'wave') {
		padding = 'pt-8 pb-16 tablet:pt-12 tablet:pb-24 desktop:pb-32';
	} else {
		padding = 'py-3.5 tablet:py-6 desktop:py-12';
	}

	return (
		<div
			id={id}
			data-variant='titleimg'
			className={`minos-grid relative bg-minos-events-bg-color ${padding}`}
		>
			<div data-aos='fade-up' className={`relative z-[11] col-start-2 col-end-14 ${blockStyle}`}>
				{/* mobile breakpoints handle in component */}
				<Heading {...headingProps} />

				{posts && (
					<div className='grid grid-cols-4 gap-y-5 tablet:grid-cols-12 tablet:gap-8'>
						{posts.map((post: QueryPost) => (
							<CardTilteImgEvents
								key={post.ID}
								post={post}
								textLabel={textLabel}
								cardType={cardType}
								readMoreStyle={readMoreStyle}
								contentFontClass={contentFontClass}
								iconType={iconType}
								displayExcerpt={displayExcerpt}
								displayDate={displayDate}
								displayPlace={displayPlace}
								displayOrganizer={displayOrganizer}
								displayTag={displayTag}
								displayPrice={displayPrice}
							/>
						))}
					</div>
				)}

				{archiveLink?.show && (
					<div className='m-auto mt-5 block w-fit'>
						{linkIndex && (
							<Button
								label={archiveLink.label}
								url={linkIndex}
								style={archiveLink.type}
								urlType={'link'}
								urlOpenNewTab={archiveLink.target}
								btnHasIcon={archiveLinkStyles?.btnHasIcon}
								btnDecorationElement={archiveLinkStyles?.btnDecorationElement}
								className='minos-events-btn-archivelink tablet:minos-events-btn-archivelink'
								animation={archiveLinkStyles?.animation}
								btnClassName={getArchiveBtnSettingsToCSS(archiveLinkStyles, 'events')}
							/>
						)}
					</div>
				)}
			</div>
		</div>
	);
}

export const CardTilteImgEvents = (props: CardEventsType) => {
	const {
		post,
		textLabel,
		cardType,
		readMoreStyle,
		contentFontClass,
		iconType,
		displayExcerpt,
		displayDate,
		displayPlace,
		displayOrganizer,
		displayTag,
		displayPrice,
	} = props;

	let cardStyle = '';
	if (cardType === 'block') {
		cardStyle =
			'desktop:p-6 tablet:p-5 p-4 bg-minos-events-bg-card-color border-minos-events-card-border-width rounded-minos-events-card border-minos-events-card-border-color';
	} else {
		cardStyle = '';
	}

	let classReadMoreButton = '';
	if (readMoreStyle?.btnIsBasicLink) {
		classReadMoreButton = '';
	} else {
		classReadMoreButton =
			'group-hover:border-[color:var(--m-events-btn-readmore-border-color-hover)] group-hover:bg-[color:var(--m-events-btn-readmore-bg-color-hover)]';
	}

	return (
		<Link
			href={post.permalink ? post.permalink : ''}
			key={post.ID}
			className={`group col-span-full flex flex-col gap-3 transition-all duration-300 ease-in-out tablet:col-span-6 desktop:col-span-4 ${cardStyle}`}
		>
			{post.image?.url ? (
				<div className='relative mb-4 tablet:mb-7'>
					<div className='relative z-20 block w-full overflow-hidden rounded-minos-events-img transition-all duration-300 ease-in-out'>
						<div
							className='absolute left-0 top-0 z-10 size-full rounded-minos-events-img bg-gradient-to-t from-minos-events-img-opacity-color to-transparent'
							aria-hidden='true'
						></div>

						<img
							className='aspect-[5/3] w-full object-cover transition-all duration-300 ease-in-out group-hover:scale-110 '
							src={post.image?.url}
							alt=''
						/>
					</div>
					<h3
						className={`absolute inset-x-3 bottom-2.5 z-20 tablet:inset-x-5 desktop:inset-x-6 ${contentFontClass?.eventTitle}`}
					>
						{post.post_title}
					</h3>
				</div>
			) : (
				<h3 className={contentFontClass?.eventTitle}>{post.post_title}</h3>
			)}

			<div className={`mb-0 tablet:mb-3.5 ${post.image?.url ? '' : 'mb-4'}`}>
				{displayDate && (
					<>
						{post.start_date && (
							<div
								className={`mt-0 flex w-fit flex-wrap items-center gap-x-2 ${contentFontClass?.eventDate}`}
							>
								{iconType === 'fill' ? (
									<DateIconFilled
										width='14'
										height='13'
										className='inline-block text-minos-events-icon-color'
									/>
								) : (
									<DateIcon width='14' height='13' className='inline-block text-minos-events-icon-color' />
								)}

								<time dateTime={post.start_date}>{post.start_date_format}</time>

								{post.end_date_format && (
									<>
										<span aria-hidden>{'\u{003E}'}</span>
										<time dateTime={post.end_date}>{post.end_date_format}</time>
									</>
								)}
							</div>
						)}
					</>
				)}

				{displayExcerpt && (
					<>
						{post.post_excerpt && (
							<p
								className={`mb-3 flex gap-x-2 tablet:mb-3.5 tablet:mt-2 ${contentFontClass?.eventExcerpt}`}
							>
								{post.post_excerpt}
							</p>
						)}
					</>
				)}

				{displayPlace && (
					<>
						{post.venue?.length ? (
							<div className={`mb-3 flex gap-x-2 last:mb-0 ${contentFontClass?.eventInfo}`}>
								<div aria-hidden>
									{iconType === 'fill' ? (
										<LocalizationIconFilled
											width='12'
											height='12'
											className='inline-block text-minos-events-icon-color'
										/>
									) : (
										<LocalizationIcon
											width='14'
											height='14'
											className='inline-block text-minos-events-icon-color'
										/>
									)}
								</div>

								<div className='inline-flex grow flex-wrap gap-x-2'>
									{post.venue?.map((place: string, index: number) => {
										return (
											<p
												key={index}
												className={`inline-flex after:content-[','] last:after:content-[''] ${contentFontClass?.eventInfo}`}
											>
												{place}
											</p>
										);
									})}
								</div>
							</div>
						) : (
							''
						)}
					</>
				)}

				{displayOrganizer && (
					<>
						{post.organizer?.length ? (
							<div className={`mb-3 flex gap-x-2 last:mb-0 ${contentFontClass?.eventInfo}`}>
								<div aria-hidden>
									{iconType === 'fill' ? (
										<PersonIconFilled
											width='15'
											height='20'
											className='inline-block text-minos-events-icon-color'
										/>
									) : (
										<PersonIcon
											width='15'
											height='20'
											className='inline-block text-minos-events-icon-color'
										/>
									)}
								</div>

								<div className='inline-flex grow flex-wrap gap-x-2'>
									{post.organizer?.map((orga: string, index: number) => {
										return (
											<p
												key={index}
												className={`inline-flex after:content-[','] last:after:content-[''] ${contentFontClass?.eventInfo}`}
											>
												{orga}
											</p>
										);
									})}
								</div>
							</div>
						) : (
							''
						)}
					</>
				)}

				{displayPrice && (
					<>
						{post.price && (
							<div className={`mb-3 flex gap-x-2 last:mb-0 ${contentFontClass?.eventInfo}`}>
								{iconType === 'fill' ? (
									<PriceIconFilled className='inline-block text-minos-events-icon-color' />
								) : (
									<PriceIcon width='16' height='14' className='inline-block text-minos-events-icon-color' />
								)}
								<p className={`inline-flex ${contentFontClass?.eventInfo}`}>{post.price}</p>
							</div>
						)}
					</>
				)}

				{displayTag && (
					<>
						{post.tag?.length ? (
							<div className={`mb-3 flex gap-x-2 last:mb-0 ${contentFontClass?.eventInfo}`}>
								<div aria-hidden>
									{iconType === 'fill' ? (
										<TagIconFilled
											width='12'
											height='12'
											className='inline-block text-minos-events-icon-color'
										/>
									) : (
										<TagIcon width='14' height='16' className='inline-block text-minos-events-icon-color' />
									)}
								</div>
								<div className='inline-flex grow flex-wrap gap-x-2'>
									{post.tag?.map((label: string, index: number) => {
										return (
											<p
												key={index}
												className={`inline-flex after:content-[','] last:after:content-[''] ${contentFontClass?.eventInfo}`}
											>
												{label}
											</p>
										);
									})}
								</div>
							</div>
						) : (
							''
						)}
					</>
				)}
			</div>

			<ReadMoreBtn
				textLabel={textLabel}
				readMoreStyle={getReadMoreBtnSettingsToCSS(readMoreStyle, 'events')}
				className={`mt-auto ${classReadMoreButton} group-hover:text-[color:var(--m-events-btn-readmore-label-color-hover)]`}
			/>
		</Link>
	);
};
